import { useEffect, useRef, useCallback } from 'react';
import AudioPlayer from '@/shared/utils/audio-player';
import { UseAudioPlayerProps, UseAudioPlayerReturn } from '../types/type';
import { PlaybackRequestGuard } from './playback-request-guard';

export const useAudioPlayer = ({
  config = {
    autoPlay: false,
    loop: false,
    muted: false,
    isBase64: true
  },
  callbacks
}: UseAudioPlayerProps = {}): UseAudioPlayerReturn => {
  const audioPlayerRef = useRef<AudioPlayer | null>(null);
  const isInitializedRef = useRef(false);
  const playbackRequestGuardRef = useRef(new PlaybackRequestGuard());

  // config와 callbacks를 useRef로 관리하여 불필요한 재생성 방지
  const configRef = useRef(config);
  const callbacksRef = useRef(callbacks);

  // config와 callbacks 값 업데이트
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    callbacksRef.current = callbacks;

    // callbacks 변경 시 기존 AudioPlayer에 새 콜백 설정
    if (audioPlayerRef.current && isInitializedRef.current) {
      audioPlayerRef.current.onloadeddata = callbacks?.onLoadedData || undefined;
    }
  }, [callbacks]);

  // AudioPlayer 초기화 - 한 번만 실행
  useEffect(() => {
    if (typeof window === 'undefined' || isInitializedRef.current) return;

    audioPlayerRef.current = new AudioPlayer(configRef.current);

    // 콜백 이벤트 설정
    if (callbacksRef.current?.onLoadedData) {
      audioPlayerRef.current.onloadeddata = callbacksRef.current.onLoadedData;
    }

    isInitializedRef.current = true;

    return () => {
      playbackRequestGuardRef.current.cancel();
      if (audioPlayerRef.current) {
        audioPlayerRef.current.destroy();
        audioPlayerRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, []); // 의존성 배열에서 config, callbacks 제거

  // 오디오 재생
  const play = useCallback(async (src?: string) => {
    if (!audioPlayerRef.current) return;
    const playbackRequest = playbackRequestGuardRef.current.begin();

    try {
      const result = await audioPlayerRef.current.play(src || '', () => {
        if (playbackRequestGuardRef.current.isCurrent(playbackRequest)) {
          callbacksRef.current?.onPlay?.();
        }
      });
      if (!playbackRequestGuardRef.current.isCurrent(playbackRequest)) return;

      if (result === false) {
        console.warn('🔊 [useAudioPlayer] play 실패 - 다음 청크로 이동');
        callbacksRef.current?.onError?.('Audio playback failed');
      }
      callbacksRef.current?.onEnded?.();
    } catch (error) {
      console.error('🔊 오디오 재생 실패:', error);
      if (playbackRequestGuardRef.current.isCurrent(playbackRequest)) {
        callbacksRef.current?.onEnded?.();
      }
    }
  }, []);

  // 오디오 일시정지
  const pause = useCallback((fadeOutDuration?: number) => {
    playbackRequestGuardRef.current.cancel();
    if (!audioPlayerRef.current) return;
    audioPlayerRef.current.pause(fadeOutDuration);
    callbacksRef.current?.onPause?.();
  }, []);

  // 오디오 소스 설정
  const setSrc = useCallback((src: string) => {
    playbackRequestGuardRef.current.cancel();
    if (!audioPlayerRef.current) return;
    audioPlayerRef.current.setSrc(src);
  }, []);

  // 오디오 리셋
  const reset = useCallback(() => {
    playbackRequestGuardRef.current.cancel();
    if (!audioPlayerRef.current) return;
    audioPlayerRef.current.reset();
  }, []);

  // 볼륨 설정
  const setVolume = useCallback((volume: number) => {
    if (!audioPlayerRef.current) return;
    audioPlayerRef.current.setVolume(volume);
  }, []);

  // AudioPlayer 제거
  const destroy = useCallback(() => {
    playbackRequestGuardRef.current.cancel();
    if (!audioPlayerRef.current) return;
    audioPlayerRef.current.destroy();
    audioPlayerRef.current = null;
    isInitializedRef.current = false;
  }, []);

  return {
    // AudioPlayer 인스턴스
    audioPlayer: audioPlayerRef.current,

    // 상태
    isInitialized: isInitializedRef.current,

    // 메서드
    play,
    pause,
    setSrc,
    reset,
    setVolume,
    destroy,
  };
};
